import { Mark, mergeAttributes } from '@tiptap/core'
import { formatPlaceholderLabel } from './formatPlaceholderLabel'
import { placeholderProtectionPlugin } from './placeholderPlugin'

const hasPlaceholder = (node: any, marks: readonly any[]) =>
  marks.some((m) => m.type.name === 'placeholder') || node?.marks?.some((m: any) => m.type.name === 'placeholder')

const getPlaceholderStart = (pos: number, nodeAfter: any, nodeBefore: any, doc: any): number => {
  if (nodeBefore?.isText && hasPlaceholder(nodeBefore, nodeBefore.marks || [])) return pos - nodeBefore.nodeSize
  if (nodeAfter?.isText && hasPlaceholder(nodeAfter, nodeAfter.marks || [])) return pos
  let start = pos
  while (start > 0) {
    const r = doc.resolve(start - 1)
    const tn = r.nodeAfter?.isText ? r.nodeAfter : r.nodeBefore?.isText ? r.nodeBefore : null
    if (!hasPlaceholder(tn, r.marks())) break
    start--
  }
  return start
}

const getPlaceholderEnd = (pos: number, nodeAfter: any, nodeBefore: any, doc: any): number => {
  if (nodeAfter?.isText && hasPlaceholder(nodeAfter, nodeAfter.marks || [])) return pos + nodeAfter.nodeSize
  if (nodeBefore?.isText && hasPlaceholder(nodeBefore, nodeBefore.marks || [])) return pos
  let end = pos
  while (end < doc.content.size) {
    const r = doc.resolve(end)
    const tn = r.nodeAfter?.isText ? r.nodeAfter : r.nodeBefore?.isText ? r.nodeBefore : null
    if (!hasPlaceholder(tn, r.marks())) break
    if (++end >= doc.content.size) break
  }
  return end
}

export const TemplatePlaceholderMark = Mark.create({
  name: 'placeholder',

  inclusive: false,

  addAttributes() {
    return {
      key: { default: null },
      label: { default: null },
      /** Shown when the value is missing; stored as `{{key|fallback}}`. */
      fallback: { default: null }
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span.template-placeholder',
        getAttrs: (node) => {
          const el = node as HTMLElement
          const dataKey = el.getAttribute('data-key')
          const label = el.getAttribute('data-label')
          const fallback = el.getAttribute('data-fb') || null
          if (dataKey) return { key: dataKey, label, fallback }
          const textContent = el.textContent || ''
          const match = textContent.match(/^\{\{(\w+)\}\}$/)
          return { key: match ? match[1] : textContent, label: match ? null : textContent }
        }
      }
    ]
  },

  renderHTML({ HTMLAttributes, mark }) {
    const key = mark.attrs?.key || ''
    const label = mark.attrs?.label || null
    const fallback = mark.attrs?.fallback || null
    const displayText = formatPlaceholderLabel(key, label)
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'template-placeholder',
        'data-key': key,
        ...(label ? { 'data-label': label } : {}),
        ...(fallback ? { 'data-fb': fallback } : {}),
        contenteditable: 'false'
      }),
      displayText || key
    ]
  },

  addProseMirrorPlugins() {
    return [placeholderProtectionPlugin()]
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { editor } = this
        const { state } = editor
        const { selection } = state
        const { $from, empty } = selection
        if (!empty) return false

        const pos = $from.pos
        const na = $from.nodeAfter
        const nb = $from.nodeBefore
        const tn = na?.isText ? na : nb?.isText ? nb : null

        if (hasPlaceholder(tn, $from.marks())) {
          let start: number, end: number
          const beforeIsText = nb?.isText && !(nb.marks || []).some((m: any) => m.type.name === 'placeholder')
          if (na?.isText && hasPlaceholder(na, na.marks || []) && beforeIsText) {
            return false
          }
          if (na?.isText && hasPlaceholder(na, na.marks || [])) {
            start = pos
            end = pos + na.nodeSize
          } else if (nb?.isText && hasPlaceholder(nb, nb.marks || [])) {
            start = pos - nb.nodeSize
            end = pos
          } else {
            start = pos
            while (start > 0) {
              const r = state.doc.resolve(start - 1)
              const t = r.nodeAfter?.isText ? r.nodeAfter : r.nodeBefore?.isText ? r.nodeBefore : null
              if (!hasPlaceholder(t, r.marks())) break
              start--
            }
            end = pos
            while (end < state.doc.content.size) {
              const r = state.doc.resolve(end)
              const t = r.nodeAfter?.isText ? r.nodeAfter : r.nodeBefore?.isText ? r.nodeBefore : null
              if (!hasPlaceholder(t, r.marks()) || ++end >= state.doc.content.size) break
            }
          }
          editor.commands.deleteRange({ from: start, to: end })
          return true
        }

        if (pos > 0) {
          const prevPos = pos - 1
          const pr = state.doc.resolve(prevPos)
          if (pr.marks().some((m) => m.type.name === 'placeholder')) {
            const start = getPlaceholderStart(prevPos, pr.nodeAfter, pr.nodeBefore, state.doc)
            const end = getPlaceholderEnd(prevPos, pr.nodeAfter, pr.nodeBefore, state.doc)
            editor.commands.deleteRange({ from: start, to: end })
            return true
          }
        }

        return false
      }
    }
  }
})
